// Desktop Notifications API
// Provides native OS notifications when browser is open but not focused

export type NotificationPermissionStatus = 'granted' | 'denied' | 'default';

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string; // Unique ID to prevent duplicates
  requireInteraction?: boolean; // Keep notification until user clicks
  silent?: boolean;
  data?: any; // Custom data
}

/**
 * Check if notifications are supported by the browser
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermissionStatus {
  if (!isNotificationSupported()) return 'denied';
  return Notification.permission;
}

/**
 * Request notification permission from user
 * Shows browser prompt asking for permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionStatus> {
  if (!isNotificationSupported()) {
    console.warn('Notifications not supported in this browser');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Show a desktop notification
 * Returns the Notification object or null if failed
 */
export function showNotification(options: NotificationOptions): Notification | null {
  if (!isNotificationSupported()) {
    console.warn('Notifications not supported');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted');
    return null;
  }

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/whipt-logo.png',
      badge: options.badge || '/whipt-icon.png',
      tag: options.tag,
      requireInteraction: options.requireInteraction || false,
      silent: options.silent || false,
      data: options.data
    });

    // Auto-close after 5 seconds unless requireInteraction is true
    if (!options.requireInteraction) {
      setTimeout(() => notification.close(), 5000);
    }

    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
}

/**
 * Show notification for new message
 */
export function notifyNewMessage(senderName: string, messagePreview: string) {
  return showNotification({
    title: `New message from ${senderName}`,
    body: messagePreview,
    tag: `message-${Date.now()}`,
    requireInteraction: false,
    data: { type: 'message', sender: senderName }
  });
}

/**
 * Show notification for upcoming deadline
 */
export function notifyUpcomingDeadline(taskTitle: string, dueDate: string) {
  return showNotification({
    title: 'Upcoming Deadline',
    body: `${taskTitle} is due ${dueDate}`,
    tag: `deadline-${taskTitle}`,
    requireInteraction: true,
    data: { type: 'deadline', task: taskTitle }
  });
}

/**
 * Show notification for class starting soon
 */
export function notifyClassStarting(className: string, minutesUntil: number) {
  return showNotification({
    title: 'Class Starting Soon',
    body: `${className} starts in ${minutesUntil} minutes`,
    tag: `class-${className}`,
    requireInteraction: false,
    data: { type: 'class', name: className }
  });
}

/**
 * Show notification for new assignment
 */
export function notifyNewAssignment(courseName: string, assignmentTitle: string) {
  return showNotification({
    title: `New Assignment: ${courseName}`,
    body: assignmentTitle,
    tag: `assignment-${assignmentTitle}`,
    requireInteraction: false,
    data: { type: 'assignment', course: courseName }
  });
}

/**
 * Save notification preference to localStorage
 */
export function saveNotificationPreference(enabled: boolean) {
  localStorage.setItem('whipt_notifications_enabled', enabled.toString());
}

/**
 * Get notification preference from localStorage
 */
export function getNotificationPreference(): boolean {
  const pref = localStorage.getItem('whipt_notifications_enabled');
  return pref === 'true' || pref === null; // Default to enabled
}

/**
 * Check if user has notifications enabled AND permission granted
 */
export function areNotificationsEnabled(): boolean {
  return getNotificationPreference() && getNotificationPermission() === 'granted';
}

/**
 * NotificationManager - centralized notification system
 */
export const NotificationManager = {
  show(type: 'message' | 'deadline' | 'class' | 'assignment', options: Partial<NotificationOptions> & { title: string; body: string }) {
    if (!areNotificationsEnabled()) return null;
    
    return showNotification({
      title: options.title,
      body: options.body,
      icon: options.icon || '/pronoia-wellness-hero2.webp',
      tag: options.tag || `${type}-${Date.now()}`,
      requireInteraction: options.requireInteraction || false,
      data: { type, ...options.data }
    });
  },
  
  requestPermission: requestNotificationPermission,
  isEnabled: areNotificationsEnabled,
  getPermission: getNotificationPermission,
  savePreference: saveNotificationPreference,
  getPreference: getNotificationPreference
};
